import { useState, useEffect, useRef } from 'react'
import { SettingsService } from '@/lib/services/settingsService'

export interface SiteSettings {
  site_name: string
  site_description: string
  auth_logo?: string
  sidenav_logo?: string
}

const defaultSettings: SiteSettings = {
  site_name: import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
  site_description: '',
  auth_logo: '/assets/img/philtower-logo.png',
  sidenav_logo: '/assets/img/philtower-logo.png',
}

let cachedSettings: SiteSettings | null = null
let settingsPromise: Promise<SiteSettings> | null = null
let isLoading = false
let hasAttemptedLoad = false

/**
 * Hook to load and use site settings from the database
 * Settings are cached to avoid multiple API calls
 */
export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(() => cachedSettings || defaultSettings)
  const [loading, setLoading] = useState(false)
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (hasInitialized.current) {
      return
    }
    hasInitialized.current = true

    if (cachedSettings) {
      const settingsToUse = cachedSettings
      setSettings((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(settingsToUse)) {
          return prev
        }
        return settingsToUse
      })
      setLoading(false)
      return
    }

    if (hasAttemptedLoad && !cachedSettings) {
      setLoading(false)
      return
    }

    if (isLoading || settingsPromise) {
      if (settingsPromise) {
        settingsPromise
          .then((loadedSettings) => {
            setSettings(loadedSettings)
            setLoading(false)
          })
          .catch(() => {
            hasAttemptedLoad = true
            setLoading(false)
          })
      }
      return
    }

    hasAttemptedLoad = true
    isLoading = true
    setLoading(true)
    
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        isLoading = false
        settingsPromise = null
        setLoading(false)
      }
    }, 5000)
    
    settingsPromise = SettingsService.getGeneralSettings()
      .then((response) => {
        clearTimeout(timeoutId)
        if (response.success && response.data?.site) {
          const authLogo = typeof response.data.site.auth_logo === 'string' 
            ? response.data.site.auth_logo 
            : defaultSettings.auth_logo
          const sidenavLogo = typeof response.data.site.sidenav_logo === 'string'
            ? response.data.site.sidenav_logo
            : defaultSettings.sidenav_logo
          
          const siteSettings: SiteSettings = {
            site_name: response.data.site.site_name || defaultSettings.site_name,
            site_description: response.data.site.site_description || defaultSettings.site_description,
            auth_logo: authLogo,
            sidenav_logo: sidenavLogo,
          }
          
          // Build full URLs for logos if they're relative paths
          const apiBaseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || ''
          if (siteSettings.auth_logo && !siteSettings.auth_logo.startsWith('http') && !siteSettings.auth_logo.startsWith('/')) {
            siteSettings.auth_logo = `${apiBaseUrl}/storage/${siteSettings.auth_logo}`
          } else if (siteSettings.auth_logo && siteSettings.auth_logo.startsWith('storage/')) {
            siteSettings.auth_logo = `${apiBaseUrl}/${siteSettings.auth_logo}`
          }
          
          if (siteSettings.sidenav_logo && !siteSettings.sidenav_logo.startsWith('http') && !siteSettings.sidenav_logo.startsWith('/')) {
            siteSettings.sidenav_logo = `${apiBaseUrl}/storage/${siteSettings.sidenav_logo}`
          } else if (siteSettings.sidenav_logo && siteSettings.sidenav_logo.startsWith('storage/')) {
            siteSettings.sidenav_logo = `${apiBaseUrl}/${siteSettings.sidenav_logo}`
          }
          
          cachedSettings = siteSettings
          setSettings(siteSettings)
          setLoading(false)
          return siteSettings
        }
        setLoading(false)
        return defaultSettings
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        if (error?.response?.status !== 401) {
          console.error('Failed to load site settings:', error)
        } else {
          console.warn('Site settings API returned 401, but public route should work. Using defaults.')
        }
        setLoading(false)
        return defaultSettings
      })
      .finally(() => {
        isLoading = false
        settingsPromise = null
      })
  }, [])

  const refresh = () => {
    cachedSettings = null
    settingsPromise = null
    hasAttemptedLoad = false
    isLoading = false
    setLoading(true)
    
    const apiBaseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || ''
    
    SettingsService.getGeneralSettings()
      .then((response) => {
        if (response.success && response.data?.site) {
          const authLogo = typeof response.data.site.auth_logo === 'string' 
            ? response.data.site.auth_logo 
            : defaultSettings.auth_logo
          const sidenavLogo = typeof response.data.site.sidenav_logo === 'string'
            ? response.data.site.sidenav_logo
            : defaultSettings.sidenav_logo
          
          const siteSettings: SiteSettings = {
            site_name: response.data.site.site_name || defaultSettings.site_name,
            site_description: response.data.site.site_description || defaultSettings.site_description,
            auth_logo: authLogo,
            sidenav_logo: sidenavLogo,
          }
          
          if (siteSettings.auth_logo && !siteSettings.auth_logo.startsWith('http') && !siteSettings.auth_logo.startsWith('/')) {
            siteSettings.auth_logo = `${apiBaseUrl}/storage/${siteSettings.auth_logo}`
          } else if (siteSettings.auth_logo && siteSettings.auth_logo.startsWith('storage/')) {
            siteSettings.auth_logo = `${apiBaseUrl}/${siteSettings.auth_logo}`
          }
          
          if (siteSettings.sidenav_logo && !siteSettings.sidenav_logo.startsWith('http') && !siteSettings.sidenav_logo.startsWith('/')) {
            siteSettings.sidenav_logo = `${apiBaseUrl}/storage/${siteSettings.sidenav_logo}`
          } else if (siteSettings.sidenav_logo && siteSettings.sidenav_logo.startsWith('storage/')) {
            siteSettings.sidenav_logo = `${apiBaseUrl}/${siteSettings.sidenav_logo}`
          }
          
          cachedSettings = siteSettings
          setSettings(siteSettings)
        } else {
          setSettings(defaultSettings)
        }
      })
      .catch((error) => {
        console.error('Failed to refresh site settings:', error)
        setSettings(defaultSettings)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return { settings, loading, refresh }
}

